import { useEffect, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { ScreenerTickerData, ScreenerOrderbookSource } from '@/types'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { OrderbookSourcePicker } from './OrderbookSourcePicker'
import { compressOrderbookLevels, inferOrderbookTickSize, type OrderbookCompression, type OrderbookDisplayLevel } from './orderbookCompression'
import { OrderbookCompressionControl } from './OrderbookCompressionControl'
import { PresetControls } from '@/components/common/PresetControls'
import type { NamedPresetControls } from '@/hooks/useNamedPresets'

interface ScreenerOrderbookPanelProps {
  standalone?: boolean
  presets?: NamedPresetControls
  symbol: string
  tickerData: ScreenerTickerData | null
  selectedSourceIds: string[]
  onSelectedSourceIdsChange: (sourceIds: string[]) => void
  collapsedSourceIds: string[]
  onCollapsedSourceIdsChange: (sourceIds: string[]) => void
  compression: OrderbookCompression
  onCompressionChange: (compression: OrderbookCompression) => void
  compressionOverrides: Record<string, OrderbookCompression>
  onCompressionOverridesChange: (overrides: Record<string, OrderbookCompression>) => void
  configuredSourceIds: string[]
  availableExchanges: string[]
}

type SelectedOrderbookEntry = {
  id: string
  exchange: string
  market: string
  sourceData: ScreenerOrderbookSource | null
  referencePrice: number | null
  inactive: boolean
}

function formatCompactUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toFixed(0)
}

function formatPrice(price: number): string {
  if (!Number.isFinite(price) || price < 0) return '-'
  if (price === 0) return '0'
  return price.toLocaleString(undefined, { maximumSignificantDigits: 21 })
}

function normalizeOrderbookSources(tickerData: ScreenerTickerData | null): ScreenerOrderbookSource[] {
  if (!tickerData) return []

  const direct = tickerData.orderbook_sources
  if (Array.isArray(direct)) {
    return direct
      .filter((item) => item && item.exchange)
      .map((item) => ({
        ...item,
        id: item.id || `${item.exchange}:${item.market || 'unknown'}`,
        market: item.market || 'unknown',
      }))
  }

  const fallback: ScreenerOrderbookSource[] = []
  for (const [exchange, exchangeData] of Object.entries(tickerData.exchanges || {})) {
    const orderbook = exchangeData.orderbook
    if (!orderbook) continue
    const market = String(orderbook.market || exchangeData.market || 'unknown').toLowerCase()
    fallback.push({
      id: `${exchange}:${market}`,
      exchange,
      market,
      tick_size: orderbook.tick_size,
      updated_at: orderbook.updated_at ?? exchangeData.updated_at ?? null,
      bids: orderbook.bids || [],
      asks: orderbook.asks || [],
    })
  }
  return fallback
}

function parseOrderbookSourceId(sourceId: string): { exchange: string; market: string } {
  const [exchange = 'unknown', market = 'unknown'] = String(sourceId).toLowerCase().split(':')
  return { exchange, market }
}

export function ScreenerOrderbookPanel({
  standalone = false,
  presets,
  symbol,
  tickerData,
  selectedSourceIds,
  onSelectedSourceIdsChange,
  collapsedSourceIds,
  onCollapsedSourceIdsChange,
  compression,
  onCompressionChange,
  compressionOverrides,
  onCompressionOverridesChange,
  configuredSourceIds,
  availableExchanges,
}: ScreenerOrderbookPanelProps) {
  const allSources = useMemo(() => normalizeOrderbookSources(tickerData), [tickerData])
  const sourceById = useMemo(
    () => new Map(allSources.map((source) => [source.id.toLowerCase(), source])),
    [allSources]
  )
  const normalizedConfiguredSourceIds = useMemo(
    () => configuredSourceIds.map((sourceId) => sourceId.toLowerCase()),
    [configuredSourceIds]
  )
  const availableExchangeSet = useMemo(
    () => new Set((availableExchanges || []).map((exchange) => exchange.toLowerCase())),
    [availableExchanges]
  )
  const referencePriceByExchange = useMemo(() => {
    const prices = new Map<string, number>()
    for (const [exchange, exchangeData] of Object.entries(tickerData?.exchanges || {})) {
      const price = Number(exchangeData.last_price)
      if (Number.isFinite(price) && price > 0) prices.set(exchange.toLowerCase(), price)
    }
    return prices
  }, [tickerData?.exchanges])
  const allKnownSourceIds = useMemo(() => {
    const ids: string[] = []
    const seen = new Set<string>()
    for (const sourceId of [...normalizedConfiguredSourceIds, ...(standalone ? selectedSourceIds : [])]) {
      if (!seen.has(sourceId)) {
        seen.add(sourceId)
        ids.push(sourceId)
      }
    }
    for (const source of allSources) {
      const sourceId = source.id.toLowerCase()
      if (!seen.has(sourceId)) {
        seen.add(sourceId)
        ids.push(sourceId)
      }
    }
    return ids
  }, [allSources, normalizedConfiguredSourceIds, standalone, selectedSourceIds])
  useEffect(() => {
    if (allKnownSourceIds.length === 0) {
      if (selectedSourceIds.length > 0) onSelectedSourceIdsChange([])
      if (collapsedSourceIds.length > 0) onCollapsedSourceIdsChange([])
      return
    }

    const valid = selectedSourceIds
      .map((id) => String(id).toLowerCase())
      .filter((id) => allKnownSourceIds.includes(id))
    if (valid.length !== selectedSourceIds.length || valid.some((id, index) => id !== selectedSourceIds[index])) {
      onSelectedSourceIdsChange(valid)
      return
    }

    const validCollapsed = collapsedSourceIds
      .map((id) => String(id).toLowerCase())
      .filter((id) => valid.includes(id))
    if (validCollapsed.length !== collapsedSourceIds.length || validCollapsed.some((id, index) => id !== collapsedSourceIds[index])) {
      onCollapsedSourceIdsChange(validCollapsed)
      return
    }
  }, [
    allKnownSourceIds,
    collapsedSourceIds,
    onCollapsedSourceIdsChange,
    onSelectedSourceIdsChange,
    selectedSourceIds,
  ])

  const selectedEntries = useMemo<SelectedOrderbookEntry[]>(
    () =>
      selectedSourceIds.map((sourceId) => {
        const normalizedId = sourceId.toLowerCase()
        const sourceData = sourceById.get(normalizedId) || null
        const parsed = parseOrderbookSourceId(normalizedId)
        const exchange = sourceData?.exchange || parsed.exchange
        const market = sourceData?.market || parsed.market
        const hasTicker = availableExchangeSet.has(exchange.toLowerCase())
        const hasData = Boolean(sourceData)
        return {
          id: normalizedId,
          exchange,
          market,
          sourceData,
          referencePrice: referencePriceByExchange.get(exchange.toLowerCase()) ?? null,
          inactive: !hasTicker || !hasData,
        }
      }),
    [availableExchangeSet, referencePriceByExchange, selectedSourceIds, sourceById]
  )

  const sourceOptions = useMemo(
    () =>
      allKnownSourceIds
        .map((sourceId) => {
          const sourceData = sourceById.get(sourceId)
          const parsed = parseOrderbookSourceId(sourceId)
          return {
            id: sourceId,
            exchange: sourceData?.exchange || parsed.exchange,
            market: sourceData?.market || parsed.market,
          }
        }),
    [allKnownSourceIds, sourceById]
  )

  const handleRemove = (sourceId: string) => {
    onSelectedSourceIdsChange(selectedSourceIds.filter((item) => item.toLowerCase() !== sourceId.toLowerCase()))
    onCollapsedSourceIdsChange(collapsedSourceIds.filter((item) => item.toLowerCase() !== sourceId.toLowerCase()))
  }

  const handleToggleSource = (sourceId: string) => {
    if (selectedSourceIds.some((item) => item.toLowerCase() === sourceId.toLowerCase())) {
      handleRemove(sourceId)
    } else {
      onSelectedSourceIdsChange([...selectedSourceIds, sourceId])
    }
  }

  const handleToggleCollapsed = (sourceId: string) => {
    if (collapsedSourceIds.includes(sourceId)) {
      onCollapsedSourceIdsChange(collapsedSourceIds.filter((item) => item !== sourceId))
      return
    }
    onCollapsedSourceIdsChange([...collapsedSourceIds, sourceId])
  }

  const handleMove = (sourceId: string, direction: -1 | 1) => {
    const currentIdx = selectedSourceIds.indexOf(sourceId)
    if (currentIdx < 0) return
    const nextIdx = currentIdx + direction
    if (nextIdx < 0 || nextIdx >= selectedSourceIds.length) return
    const next = [...selectedSourceIds]
      ;[next[currentIdx], next[nextIdx]] = [next[nextIdx], next[currentIdx]]
    onSelectedSourceIdsChange(next)
  }

  return (
    <aside aria-label="Orderbook sources" className={`min-h-0 min-w-0 w-full bg-background/70 backdrop-blur-sm flex flex-col ${standalone ? 'flex-1' : ''}`}>
      <div className="h-14 shrink-0 px-3 border-b border-border/40 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Orderbook</div>
          <div className="text-sm font-semibold">{symbol || 'Ticker'}</div>
        </div>
        <Badge variant="secondary" className="text-[10px]">
          {selectedEntries.length}
        </Badge>
      </div>

      {presets && <PresetControls label="orderbook" presets={presets} className="shrink-0 border-b border-border/30 px-2 py-2" />}

      <OrderbookSourcePicker
        sources={sourceOptions}
        selectedSourceIds={selectedSourceIds}
        onToggle={handleToggleSource}
      />

      <div className="shrink-0 px-2 py-2 border-b border-border/30 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Compression</div>
          <div className="text-[9px] text-muted-foreground/70">Default for all orderbooks</div>
        </div>
        <OrderbookCompressionControl value={compression} onChange={onCompressionChange} label="All orderbooks" />
      </div>

      <div className={standalone
        ? "grid min-h-0 flex-1 items-start gap-3 overflow-y-auto p-3 custom-scrollbar grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))]"
        : "min-h-0 flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar"}>
        {selectedEntries.length === 0 ? (
          <div className="h-full min-h-24 flex items-center justify-center text-xs text-muted-foreground">
            Check a market above to show its orderbook.
          </div>
        ) : (
          selectedEntries.map((entry, index) => (
            <OrderbookSourceCard
              key={entry.id}
              source={entry.sourceData}
              exchange={entry.exchange}
              market={entry.market}
              inactive={entry.inactive}
              compression={compressionOverrides[entry.id] ?? compression}
              inheritedCompression={!Object.prototype.hasOwnProperty.call(compressionOverrides, entry.id)}
              onCompressionChange={(value) => onCompressionOverridesChange({ ...compressionOverrides, [entry.id]: value })}
              onResetCompression={() => {
                const next = { ...compressionOverrides }
                delete next[entry.id]
                onCompressionOverridesChange(next)
              }}
              referencePrice={entry.referencePrice}
              collapsed={collapsedSourceIds.includes(entry.id)}
              canMoveUp={index > 0}
              canMoveDown={index < selectedEntries.length - 1}
              onToggleCollapsed={() => handleToggleCollapsed(entry.id)}
              onMoveUp={() => handleMove(entry.id, -1)}
              onMoveDown={() => handleMove(entry.id, 1)}
              onRemove={() => handleRemove(entry.id)}
            />
          ))
        )}
      </div>
    </aside>
  )
}

function OrderbookSourceCard({
  source,
  exchange,
  market,
  inactive,
  compression,
  inheritedCompression,
  onCompressionChange,
  onResetCompression,
  referencePrice,
  collapsed,
  canMoveUp,
  canMoveDown,
  onToggleCollapsed,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  source: ScreenerOrderbookSource | null
  exchange: string
  market: string
  inactive: boolean
  compression: OrderbookCompression
  inheritedCompression: boolean
  onCompressionChange: (value: OrderbookCompression) => void
  onResetCompression: () => void
  referencePrice: number | null
  collapsed: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onToggleCollapsed: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}) {
  const depthContainerRef = useRef<HTMLDivElement>(null)
  const centeredSettingRef = useRef<string | null>(null)
  const levels = useMemo<OrderbookDisplayLevel[]>(
    () => compressOrderbookLevels(source?.bids, source?.asks, compression, referencePrice, source?.tick_size),
    [compression, referencePrice, source?.asks, source?.bids, source?.tick_size],
  )

  const tickSize = useMemo(
    () => compression.mode === 'multiplier' ? inferOrderbookTickSize(source?.bids, source?.asks, source?.tick_size) : null,
    [compression.mode, source?.bids, source?.asks, source?.tick_size],
  )
  const priceStep = compression.mode === 'multiplier' && tickSize !== null
    ? Number((tickSize * compression.value).toPrecision(15)) : null
  const hasExplicitTick = Number.isFinite(source?.tick_size) && (source?.tick_size ?? 0) > 0

  const maxSize = useMemo(
    () => levels.reduce((acc, level) => Math.max(acc, level.usdt_size || 0), 0) || 1,
    [levels]
  )

  useEffect(() => {
    const container = depthContainerRef.current
    if (collapsed || !container || levels.length === 0) {
      centeredSettingRef.current = null
      return
    }
    const settingKey = `${compression.mode}:${compression.value}`
    // Recenter after a setting change or first depth, while preserving manual
    // scrolling during subsequent live snapshots.
    if (centeredSettingRef.current === settingKey) return
    const firstBid = container.querySelector<HTMLElement>('[data-orderbook-side="bid"]')
    if (firstBid) container.scrollTop = Math.max(0, firstBid.offsetTop - container.clientHeight / 2)
    centeredSettingRef.current = settingKey
  }, [collapsed, compression.mode, compression.value, levels])

  return (
    <div className="rounded-md border border-border/40 bg-card overflow-hidden">
      <div className="px-2 py-1.5 border-b border-border/30 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide">{exchange}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {market}
          </Badge>
          {inactive && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 opacity-80">
              inactive
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={!canMoveUp}
            onClick={onMoveUp}
            title="Move up"
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={!canMoveDown}
            onClick={onMoveDown}
            title="Move down"
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onToggleCollapsed} title={collapsed ? 'Expand orderbook' : 'Collapse orderbook'}>
            {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onRemove} title="Remove orderbook">
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-b border-border/30 bg-muted/20 px-2 py-1.5">
        <div className="min-w-0 text-[9px] text-muted-foreground">
          {Number.isFinite(source?.updated_at) && <div title="Original depth timestamp">
            Depth {new Date(Number(source!.updated_at) * 1000).toLocaleTimeString('en-GB', { timeZone: 'UTC', hour12: false })} UTC
          </div>}
          <div className={inheritedCompression ? '' : 'text-primary'}>{inheritedCompression ? 'Shared compression' : 'Custom compression'}</div>
          {priceStep !== null && Number.isFinite(priceStep) && (
            <div className="truncate tabular-nums" title={hasExplicitTick ? 'Price step = exchange tick size × multiplier' : 'Tick size inferred from available price precision'}>
              Step {formatPrice(priceStep)}{hasExplicitTick ? '' : ' · inferred'}
            </div>
          )}
        </div>
        <OrderbookCompressionControl
          value={compression}
          onChange={onCompressionChange}
          label={`${exchange} ${market}`}
          inherited={inheritedCompression}
          onReset={onResetCompression}
        />
      </div>

      {!collapsed && (
        <div ref={depthContainerRef} className="relative max-h-[420px] overflow-y-auto custom-scrollbar">
          {inactive ? (
            <div className="px-3 py-6 text-xs text-muted-foreground text-center">
              Ticker/orderbook data is unavailable for this exchange
            </div>
          ) : levels.length === 0 ? (
            <div className="px-3 py-6 text-xs text-muted-foreground text-center">
              {compression.mode === 'percent' && compression.value !== null ? `No depth levels within ±${compression.value}%` : 'No depth levels yet'}
            </div>
          ) : (
            levels.map((level, idx) => {
              const widthPct = Math.max(4, Math.min(100, (level.usdt_size / maxSize) * 100))
              const isBid = level.side === 'bid'
              return (
                <div key={`${level.side}-${level.price}-${idx}`} data-orderbook-side={level.side} className="relative h-5 border-b border-border/10 text-[10px]">
                  <div
                    className={isBid ? 'absolute inset-y-0 left-0 bg-emerald-600/35' : 'absolute inset-y-0 left-0 bg-red-600/35'}
                    style={{ width: `${widthPct}%` }}
                  />
                  <div className="relative z-10 h-full px-1.5 grid grid-cols-[1fr_auto] items-center text-[10px] font-medium tabular-nums">
                    <span className={isBid ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}>
                      {formatCompactUsd(level.usdt_size)}
                    </span>
                    <span className="text-foreground">{formatPrice(level.price)}</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
